import { format } from 'date-fns'
import React from 'react'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { getEtlStats } from 'src/api/nestready/requests/listing'
import { Text } from 'src/Components'
import HousingType from 'src/Components/shared/HousingType'
import { MlsLogo } from 'src/Components/homeSearch/Compliance/MlsLogo'
import FooterText from 'src/Components/homeSearch/HomeSearchFooter/FooterText/FooterText'
import { MlsComplianceData } from 'src/services/mlsComplianceDataService'
import { selectCustomerId } from 'src/store/selectors/customerProfile'
import {
  everyStateMlsComplianceData,
  EveryStateOriginatingSystemNames,
} from '../../constants/compliance/everyStateMlsComplianceData'
import styles from './MlsDisclaimers.module.scss'

export const MlsDisclaimers = () => {
  const customerId = useSelector(selectCustomerId)
  const [lastUpdatedDate, setLastUpdatedDate] = useState('')
  useEffect(() => {
    fetchAndSetLastUpdatedDate()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAndSetLastUpdatedDate = async () => {
    const etlStatsData = await getEtlStats(customerId)
    const everyStateEtlData = etlStatsData.data?.data?.find(
      d => d.dataSource === 'every_state'
    )
    setLastUpdatedDate(everyStateEtlData.createdAt)
  }

  const mlsKeys = Object.keys(everyStateMlsComplianceData)
  const refs = mlsKeys.reduce((acc, value) => {
    acc[value] = React.createRef()
    return acc
  }, {})

  useEffect(() => {
    // scroll to mls based on hash in URL
    const decodedHash = decodeURIComponent(window.location.hash)
    if (!decodedHash) {
      return
    }
    const mlsKey = decodedHash?.replace('#', '')
    refs[mlsKey].current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    })
  }, [refs])

  function getCopyrightMarkupOrString(mls: MlsComplianceData) {
    if (mls?.copyrightMarkup) {
      // will need to sanitize HTML if this moves to external source
      const htmlString = mls.copyrightMarkup
      return (
        <div
          className={styles.danger}
          dangerouslySetInnerHTML={{ __html: htmlString }}
        />
      )
    } else {
      return mls?.copyright
    }
  }
  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {Object.entries(everyStateMlsComplianceData)
          .sort()
          .filter(([mlsKey, mls]) => mlsKey !== 'west contra costa') // data for this is same as contra costa
          .map(([mlsKey, mls]) => (
            <div ref={refs[mlsKey]} className={styles.section} key={mlsKey}>
              <div className={styles.borderBottom}>
                <div>
                  <Text
                    size='xl'
                    weight='semibold'
                    variant='neutralCool'
                    variantCount='900'
                  >
                    {mls?.mlsName}
                  </Text>
                </div>
                <div className={styles.logoSection}>
                  <MlsLogo
                    logoClassName={styles.logo}
                    logoWrapperClassName={styles.logoWrapper}
                    listing={{
                      // We only display EveryState disclaimers on this page, so it's ok to hardcode props for now
                      source: {
                        name: 'EveryState',
                        link: '',
                      },
                      originatingSystemName:
                        mlsKey as EveryStateOriginatingSystemNames,
                      disclaimer: '',
                    }}
                  />
                </div>
                {mls?.copyright && (
                  <div className={styles.copyright}>
                    {getCopyrightMarkupOrString(mls)}
                  </div>
                )}

                {/* don't display if copyright and disclaimer are the exact same text */}
                {mls?.disclaimer && mls?.disclaimer !== mls?.copyright && (
                  <div className={styles.disclaimer}>
                    <Text variant='neutralCool' variantCount='800'>
                      {mls.disclaimer}
                    </Text>
                  </div>
                )}
              </div>

              {lastUpdatedDate && (
                <div className={styles.lastUpdatedContainer}>
                  <div className={styles.lastUpdatedText}>
                    <Text
                      weight='bold'
                      variant='neutralCool'
                      variantCount='600'
                      transform='uppercase'
                      size='xxs'
                    >
                      Last Updated
                    </Text>
                  </div>
                  <div className={styles.lastUpdatedDate}>
                    {format(new Date(lastUpdatedDate), 'MMMM dd, yyyy h:mm a')}
                  </div>
                </div>
              )}
            </div>
          ))}
        <div>
          <HousingType className={styles.housingType} />
          <FooterText className={styles.footerText} />
        </div>
      </div>
    </div>
  )
}
